/**
 * Ícones do sistema — Phosphor, num só lugar.
 *
 * Toda tela importa daqui, nunca da biblioteca: o estilo visual (peso,
 * duotone) é decidido uma vez, e trocar de família de ícones vira mudar
 * este arquivo.
 *
 * Pesos:
 *   - duotone (padrão): contorno + preenchimento suave — o visual moderno
 *     de navegação, atalhos, estados e ações;
 *   - bold: glifos de interface pequenos (fechar, check, setas, setinha de
 *     menu, busca), que no duotone ficariam borrados;
 *   - fill: passe `weight="fill"` para estados ativos (item de menu
 *     selecionado, aula curtida, botão de play).
 */
import {
  Archive as PArchive,
  ArrowCounterClockwise as PArrowCounterClockwise,
  ArrowDown as PArrowDown,
  ArrowLeft as PArrowLeft,
  ArrowRight as PArrowRight,
  ArrowSquareOut as PArrowSquareOut,
  Article as PArticle,
  Bank as PBank,
  Basket as PBasket,
  BookOpen as PBookOpen,
  Broadcast as PBroadcast,
  CalendarDots as PCalendarDots,
  CaretDown as PCaretDown,
  ChatCircle as PChatCircle,
  Check as PCheck,
  CheckCircle as PCheckCircle,
  CircleNotch as PCircleNotch,
  Clock as PClock,
  ClockCounterClockwise as PClockCounterClockwise,
  Copy as PCopy,
  Desktop as PDesktop,
  DeviceMobile as PDeviceMobile,
  DeviceTablet as PDeviceTablet,
  DownloadSimple as PDownloadSimple,
  EnvelopeSimple as PEnvelopeSimple,
  Eye as PEye,
  EyeSlash as PEyeSlash,
  FileDoc as PFileDoc,
  FileImage as PFileImage,
  FilePdf as PFilePdf,
  FilePpt as PFilePpt,
  FileText as PFileText,
  FileXls as PFileXls,
  FilmStrip as PFilmStrip,
  Flask as PFlask,
  GraduationCap as PGraduationCap,
  Handshake as PHandshake,
  Heart as PHeart,
  Hourglass as PHourglass,
  House as PHouse,
  Lightbulb as PLightbulb,
  LinkSimple as PLinkSimple,
  List as PList,
  LockSimple as PLockSimple,
  MagnifyingGlass as PMagnifyingGlass,
  MapPin as PMapPin,
  MonitorPlay as PMonitorPlay,
  Moon as PMoon,
  NotePencil as PNotePencil,
  Package as PPackage,
  PaperPlaneRight as PPaperPlaneRight,
  Pause as PPause,
  Phone as PPhone,
  Play as PPlay,
  PlayCircle as PPlayCircle,
  Plus as PPlus,
  Question as PQuestion,
  RocketLaunch as PRocketLaunch,
  ShareNetwork as PShareNetwork,
  ShieldCheck as PShieldCheck,
  ShieldWarning as PShieldWarning,
  SidebarSimple as PSidebarSimple,
  SignIn as PSignIn,
  SignOut as PSignOut,
  Sparkle as PSparkle,
  SquaresFour as PSquaresFour,
  Stack as PStack,
  Star as PStar,
  Stop as PStop,
  Storefront as PStorefront,
  Sun as PSun,
  Television as PTelevision,
  Tray as PTray,
  Trash as PTrash,
  TrendUp as PTrendUp,
  UploadSimple as PUploadSimple,
  User as PUser,
  Users as PUsers,
  Warning as PWarning,
  WarningCircle as PWarningCircle,
  X as PX,
  type Icon as PhosphorIcon,
  type IconProps,
  type IconWeight,
} from '@phosphor-icons/react';
import { forwardRef } from 'react';

/** Tipo de um componente de ícone — para props como `icon: Icon`. */
export type Icon = PhosphorIcon;
export type { IconProps, IconWeight };

function weighted(Base: PhosphorIcon, weight: IconWeight): Icon {
  const Wrapped = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
    <Base ref={ref} weight={weight} {...props} />
  ));
  Wrapped.displayName = Base.displayName;
  return Wrapped as Icon;
}

const duo = (Base: PhosphorIcon) => weighted(Base, 'duotone');
const bold = (Base: PhosphorIcon) => weighted(Base, 'bold');

// ───────────── navegação ─────────────
export const House = duo(PHouse);
export const Handshake = duo(PHandshake);
export const CalendarDots = duo(PCalendarDots);
export const MonitorPlay = duo(PMonitorPlay);
export const Storefront = duo(PStorefront);
export const Package = duo(PPackage);
export const SquaresFour = duo(PSquaresFour);
export const SidebarSimple = duo(PSidebarSimple);
export const List = bold(PList);

// ───────────── conteúdo ─────────────
export const BookOpen = duo(PBookOpen);
export const Article = duo(PArticle);
export const GraduationCap = duo(PGraduationCap);
export const Play = duo(PPlay);
export const Pause = duo(PPause);
export const PlayCircle = duo(PPlayCircle);
export const FilmStrip = duo(PFilmStrip);
export const Broadcast = duo(PBroadcast);
export const Question = duo(PQuestion);
export const Stack = duo(PStack);
export const Clock = duo(PClock);
export const ClockCounterClockwise = duo(PClockCounterClockwise);
export const Hourglass = duo(PHourglass);
export const NotePencil = duo(PNotePencil);
export const Flask = duo(PFlask);
export const Basket = duo(PBasket);
export const Bank = duo(PBank);
export const MapPin = duo(PMapPin);
export const Sparkle = duo(PSparkle);
export const TrendUp = duo(PTrendUp);
export const Tray = duo(PTray);
export const ChatCircle = duo(PChatCircle);
export const Lightbulb = duo(PLightbulb);
export const Star = duo(PStar);

// ───────────── arquivos ─────────────
export const FilePdf = duo(PFilePdf);
export const FileImage = duo(PFileImage);
export const FileDoc = duo(PFileDoc);
export const FilePpt = duo(PFilePpt);
export const FileXls = duo(PFileXls);
export const FileText = duo(PFileText);

// ───────────── ações ─────────────
export const Heart = duo(PHeart);
export const ShareNetwork = duo(PShareNetwork);
export const PaperPlaneRight = duo(PPaperPlaneRight);
export const UploadSimple = duo(PUploadSimple);
export const DownloadSimple = duo(PDownloadSimple);
export const Copy = duo(PCopy);
export const LinkSimple = duo(PLinkSimple);
export const Trash = duo(PTrash);
export const Archive = duo(PArchive);
export const RocketLaunch = duo(PRocketLaunch);
export const ArrowCounterClockwise = duo(PArrowCounterClockwise);
export const ArrowSquareOut = bold(PArrowSquareOut);
export const EnvelopeSimple = duo(PEnvelopeSimple);
export const Phone = duo(PPhone);
export const SignIn = duo(PSignIn);
export const SignOut = duo(PSignOut);
export const Eye = duo(PEye);
export const EyeSlash = duo(PEyeSlash);
export const Sun = duo(PSun);
export const Moon = duo(PMoon);
export const Stop = duo(PStop);

// ───────────── estado ─────────────
export const CheckCircle = duo(PCheckCircle);
export const Warning = duo(PWarning);
export const WarningCircle = duo(PWarningCircle);
export const ShieldCheck = duo(PShieldCheck);
export const ShieldWarning = duo(PShieldWarning);
export const LockSimple = duo(PLockSimple);
export const User = duo(PUser);
export const Users = duo(PUsers);
export const CircleNotch = bold(PCircleNotch);

// ───────────── dispositivos ─────────────
export const Desktop = duo(PDesktop);
export const DeviceMobile = duo(PDeviceMobile);
export const DeviceTablet = duo(PDeviceTablet);
export const Television = duo(PTelevision);

// ───────────── glifos de interface ─────────────
export const X = bold(PX);
export const Check = bold(PCheck);
export const Plus = bold(PPlus);
export const ArrowLeft = bold(PArrowLeft);
export const ArrowRight = bold(PArrowRight);
export const ArrowDown = bold(PArrowDown);
export const CaretDown = bold(PCaretDown);
export const MagnifyingGlass = bold(PMagnifyingGlass);
